import { isLiability, type AccountType, type Frequency, type TaxWrapper, type Transaction } from '@keel/core';

/** Human names for account types (SPEC §3). */
export const ACCOUNT_TYPE_LABELS: Readonly<Record<AccountType, string>> = {
  transaction: 'Everyday bank account', savings: 'Savings', offset: 'Offset', term_deposit: 'Term deposit', cash: 'Cash',
  credit_card: 'Credit card', loan: 'Loan', mortgage: 'Mortgage', hecs_help: 'HECS-HELP', investment: 'Brokerage / investment',
  superannuation: 'Super', crypto: 'Crypto', property: 'Property', vehicle: 'Vehicle', other_asset: 'Other asset',
  other_liability: 'Other liability', receivable: 'Money owed to you', payable: 'Money you owe',
};

/** Tax wrappers by country (an AU SMSF is an entity, not a wrapper). Sales inside a wrapper create no CGT events. */
export const TAX_WRAPPER_LABELS: Readonly<Record<TaxWrapper, string>> = {
  none: 'None (taxable)', isa: 'ISA', lisa: 'Lifetime ISA', sipp: 'SIPP', pension: 'Workplace pension',
  '401k': '401(k)', ira: 'Traditional IRA', roth_ira: 'Roth IRA', super: 'Super',
};
const WRAPPERS_BY_COUNTRY: Readonly<Record<string, readonly TaxWrapper[]>> = {
  GB: ['none', 'isa', 'lisa', 'sipp', 'pension'], US: ['none', '401k', 'ira', 'roth_ira'], AU: ['none', 'super'],
};
export const taxWrapperOptions = (country: string | undefined, current?: TaxWrapper) => {
  const list = WRAPPERS_BY_COUNTRY[country ?? 'AU'] ?? ['none'];
  return [...list, ...(current && !list.includes(current) ? [current] : [])].map((value) => ({ value, label: TAX_WRAPPER_LABELS[value] }));
};

export type AccountGroupId = 'banking' | 'investments' | 'assets' | 'credit' | 'loans';
export interface AccountGroup {
  readonly id: AccountGroupId;
  readonly label: string;
  readonly side: 'asset' | 'liability';
  readonly types: readonly AccountType[];
}

/** Display groups, assets first. Every account type belongs to exactly one group. */
export const ACCOUNT_GROUPS: readonly AccountGroup[] = [
  { id: 'banking', label: 'Cash & banking', side: 'asset', types: ['transaction', 'savings', 'offset', 'term_deposit', 'cash'] },
  { id: 'investments', label: 'Investments & super', side: 'asset', types: ['investment', 'superannuation', 'crypto'] },
  { id: 'assets', label: 'Property & other assets', side: 'asset', types: ['property', 'vehicle', 'other_asset', 'receivable'] },
  { id: 'credit', label: 'Credit cards', side: 'liability', types: ['credit_card'] },
  { id: 'loans', label: 'Loans & other debts', side: 'liability', types: ['loan', 'mortgage', 'hecs_help', 'other_liability', 'payable'] },
];

export const accountGroupOf = (type: AccountType): AccountGroup =>
  ACCOUNT_GROUPS.find((g) => g.types.includes(type)) ?? (ACCOUNT_GROUPS[isLiability(type) ? 4 : 2] as AccountGroup);

export const ACCOUNT_TYPE_OPTIONS = ACCOUNT_GROUPS.flatMap((g) => g.types.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABELS[t] })));

export const FREQUENCY_LABELS: Readonly<Record<Frequency, string>> = {
  daily: 'Daily', weekly: 'Weekly', fortnightly: 'Fortnightly', four_weekly: 'Every 4 weeks', monthly: 'Monthly',
  quarterly: 'Quarterly', half_yearly: 'Every 6 months', yearly: 'Yearly',
};
export const FREQUENCY_OPTIONS = (Object.keys(FREQUENCY_LABELS) as Frequency[]).map((f) => ({ value: f, label: FREQUENCY_LABELS[f] }));

export type TxStatus = Transaction['status'];
export const STATUS_LABELS: Readonly<Record<TxStatus, string>> = { pending: 'Pending', cleared: 'Cleared', reconciled: 'Reconciled', void: 'Void' };
export const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as TxStatus[]).map((s) => ({ value: s, label: STATUS_LABELS[s] }));

/** "1 transaction" / "3 transactions". */
export const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;
